import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Gift, Share2, CreditCard, Users, Copy, CheckCircle, FileText, AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Program Guidelines Content
const programGuidelines = {
  overview: `Customers who refer a new purchaser to D-Freeze may receive a promotional store credit after the referred customer completes a qualifying purchase.

Referral rewards:
• Are issued as store credit only
• Cannot be redeemed for cash
• Are intended as promotional rewards
• May be applied to accessories, training, upgrades, or future purchases

Rewards are typically issued 30 days after the referred purchase is completed, once the order has passed the applicable refund or cancellation period.`,

  dos: [
    {
      title: 'Share Personal Experiences',
      content: 'Customers may share their genuine experiences using D-Freeze products with friends, family, or their community. Examples include talking about freeze drying with friends, sharing product experiences on social media, and providing a referral link to someone interested in purchasing.'
    },
    {
      title: 'Use Official Referral Links or Codes',
      content: 'Customers should use the official referral link or referral code provided through the D-Freeze referral system. This helps ensure referrals are properly tracked and credited.'
    },
    {
      title: 'Refer Individuals with Genuine Interest',
      content: 'Referrals should be made to individuals who have expressed interest in freeze drying, food preservation, or related products.'
    },
    {
      title: 'Follow Platform Rules',
      content: 'Participants must comply with the policies of platforms they use when sharing referral links, including social media platforms and online communities.'
    }
  ],

  donts: [
    {
      title: 'No Cash Incentives',
      content: 'Participants may not offer cash rebates, personal payment incentives, or gift cards outside the D-Freeze program to encourage someone to purchase using their referral.'
    },
    {
      title: 'No Misrepresentation',
      content: 'Participants may not make false claims about D-Freeze products, misrepresent product capabilities, or guarantee specific business income or outcomes. All product discussions should remain accurate and based on real experience.'
    },
    {
      title: 'No Spam or Mass Promotion',
      content: 'Referral links may not be distributed through unsolicited email campaigns, spam messages, automated posting tools, or paid advertising campaigns. The program is intended for organic referrals, not large-scale marketing operations.'
    },
    {
      title: 'No Self-Referrals',
      content: 'Participants may not create multiple accounts or attempt to refer themselves to generate rewards.'
    },
    {
      title: 'No Cash Redemption',
      content: 'Referral credits cannot be exchanged for cash, cannot be transferred to another individual, and must be used within the D-Freeze store.'
    }
  ],

  rewardTiming: `Referral rewards are issued only after the referred purchase meets all qualifying conditions:
• The referred customer completes a qualifying purchase
• The purchase is paid in full
• The order is delivered and not returned
• The applicable return window has expired

Rewards are typically issued within 30 days of successful order completion.`,

  expiration: `Referral credits:
• Are valid for 12 months from the date issued
• Expire automatically if not used within that time period
• May only be used toward purchases within the D-Freeze ecosystem`,

  compliance: `This referral program is structured as a promotional customer reward program.

Participants acknowledge that:
• Rewards are promotional credits, not wages or commissions
• Rewards have no cash value outside the D-Freeze store
• Participants are responsible for complying with local laws applicable to them

D-Freeze reserves the right to review referral activity and remove participants who abuse the program.

D-Freeze reserves the right to modify the referral program structure, change reward values, or suspend or terminate the program at any time if necessary to maintain fairness, compliance, or operational integrity.`
};

const ReferAFriend = () => {
  const [customContent, setCustomContent] = useState(null);
  const [settings, setSettings] = useState({ referral_discount_percent: 5, referral_commission_percent: 3 });
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [referralCode, setReferralCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');
  const [checking, setChecking] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimedCode, setClaimedCode] = useState(null);
  
  // Opt-in states
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [hasOptedIn, setHasOptedIn] = useState(false);
  const [guidelinesRead, setGuidelinesRead] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.refer_a_friend_content) {
          setCustomContent(res.data.refer_a_friend_content);
        }
        setSettings({
          referral_discount_percent: res.data.referral_discount_percent || 5,
          referral_commission_percent: res.data.referral_commission_percent || 3
        });
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    fetchSettings();
    
    // Check if user already opted in (stored in localStorage)
    const optedIn = localStorage.getItem('referral_program_opted_in');
    if (optedIn === 'true') {
      setHasOptedIn(true);
    }
  }, []);

  const handleOptIn = () => {
    setHasOptedIn(true);
    localStorage.setItem('referral_program_opted_in', 'true');
    setShowGuidelines(false);
  };

  const createReferralCode = async (e) => {
    e.preventDefault();
    
    // Require opt-in before creating code
    if (!hasOptedIn) {
      setShowGuidelines(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/referrals/create`, {
        referrer_email: formData.email,
        referrer_name: formData.name
      });
      setReferralCode(res.data);
      fetchStats(formData.email);
    } catch (error) {
      console.error('Failed to create referral code:', error);
      alert('Failed to create referral code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingCode = async (e) => {
    e.preventDefault();
    setChecking(true);
    setCreditInfo(null);
    setClaimedCode(null);
    
    try {
      // First check for referral code
      const res = await axios.get(`${API_URL}/api/referrals/my-code/${checkEmail}`);
      setReferralCode(res.data);
      fetchStats(checkEmail);
      // Auto opt-in if they already have a code
      setHasOptedIn(true);
      localStorage.setItem('referral_program_opted_in', 'true');
      
      // Also check for credit balance
      try {
        const creditRes = await axios.post(`${API_URL}/api/referrals/check-credit`, { email: checkEmail });
        setCreditInfo(creditRes.data);
      } catch (creditErr) {
        console.log('No credit info found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No referral code, but still check for credit
        try {
          const creditRes = await axios.post(`${API_URL}/api/referrals/check-credit`, { email: checkEmail });
          setCreditInfo(creditRes.data);
          if (creditRes.data.found) {
            // They have credit but entered email might be different
            alert(`Found referral account! Your referral code is: ${creditRes.data.referral_code}`);
          }
        } catch (creditErr) {
          alert('No referral code found for this email. Create one below!');
        }
      } else {
        console.error('Failed to check referral code:', error);
      }
    } finally {
      setChecking(false);
    }
  };

  const claimCredit = async () => {
    if (!checkEmail && !referralCode?.referrer_email) {
      alert('Please enter your email first');
      return;
    }
    
    setClaiming(true);
    try {
      const email = checkEmail || referralCode?.referrer_email;
      const res = await axios.post(`${API_URL}/api/referrals/claim-credit`, { email });
      setClaimedCode(res.data);
      // Refresh credit info
      const creditRes = await axios.post(`${API_URL}/api/referrals/check-credit`, { email });
      setCreditInfo(creditRes.data);
      alert(`Success! Your store credit code is: ${res.data.credit_code}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to claim credit');
    } finally {
      setClaiming(false);
    }
  };

  const fetchStats = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/referrals/stats/${email}`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const copyCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareUrl = referralCode ? `${window.location.origin}/products?ref=${referralCode.code}` : '';

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-[#003456] to-[#002B47]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#CE8946] rounded-full flex items-center justify-center mx-auto mb-6">
            <Gift className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Refer a Friend Program</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Share the gift of freeze drying! Your friends save {settings.referral_discount_percent}%, and you receive <strong>store credit rewards</strong> after their purchase is complete.
          </p>
          <p className="text-white/60 mt-4 text-sm">
            Store credit is issued 30 days after your friend's purchase is completed.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <>
              {/* How It Works */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {[
                  { icon: Share2, step: '1', title: 'Share Your Code', description: 'Get your unique referral code and share it with friends' },
                  { icon: Gift, step: '2', title: 'Friend Gets Discount', description: `Your friend saves ${settings.referral_discount_percent}% on their freeze dryer` },
                  { icon: CreditCard, step: '3', title: 'You Get Rewards', description: `Receive ${settings.referral_commission_percent}% in store credit rewards after 30 days` }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 bg-[#003456] text-white rounded-full flex items-center justify-center font-bold">
                      {item.step}
                    </div>
                    <item.icon className="h-10 w-10 text-[#003456] mx-auto mb-3 mt-4" />
                    <p className="text-lg font-bold text-[#0a0203]">{item.title}</p>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>

              {/* Program Guidelines Link */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <FileText className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-800 mb-2">Program Guidelines - Do's and Don'ts</h3>
                    <p className="text-amber-700 text-sm mb-3">
                      Please read and accept our program guidelines before participating in the Refer-a-Friend program. 
                      This ensures fair and compliant participation for all members.
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-amber-500 text-amber-700 hover:bg-amber-100"
                      onClick={() => setShowGuidelines(true)}
                      data-testid="view-guidelines-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Program Guidelines
                    </Button>
                  </div>
                </div>
              </div>
              
              {referralCode ? (
                /* Referral Dashboard */
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Your Referral Code</h2>
                    
                    <div className="bg-[#F8FAFC] rounded-xl p-6 mb-6">
                      <p className="text-sm text-gray-500 mb-2">Share this code with friends:</p>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-bold text-[#003456] tracking-wider">{referralCode.code}</span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={copyCode}
                          className="ml-auto"
                        >
                          {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                          {copied ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                    </div>

                    {/* Program Agreement Timestamp */}
                    {referralCode.guidelines_accepted_at && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-testid="referral-agreement-timestamp">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Program Guidelines Accepted</span>
                        </div>
                        <p className="text-xs text-green-700 mt-1">
                          {new Date(referralCode.guidelines_accepted_at).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          This timestamp is recorded for compliance purposes.
                        </p>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Share link:</p>
                        <div className="flex items-center gap-2">
                          <Input value={shareUrl} readOnly className="text-sm" />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(shareUrl);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button 
                          className="flex-1 bg-[#1877f2] hover:bg-[#1864d9]"
                          onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                        >
                          Share on Facebook
                        </Button>
                        <Button 
                          className="flex-1 bg-[#1da1f2] hover:bg-[#1a8cd8]"
                          onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out D-Freeze freeze dryers! Use my code ${referralCode.code} for ${settings.referral_discount_percent}% off: ${shareUrl}`)}`, '_blank')}
                        >
                          Share on Twitter
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Your Rewards</h2>
                    
                    {stats ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-[#F8FAFC] rounded-xl p-4 text-center">
                            <Users className="h-8 w-8 text-[#003456] mx-auto mb-2" />
                            <p className="text-3xl font-bold text-[#003456]">{stats.total_referrals}</p>
                            <p className="text-gray-500 text-sm">Referrals</p>
                          </div>
                          <div className="bg-green-50 rounded-xl p-4 text-center">
                            <CreditCard className="h-8 w-8 text-green-600 mx-auto mb-2" />
                            <p className="text-3xl font-bold text-green-600">${(creditInfo?.store_credit_earned || stats.total_commission || 0).toFixed(2)}</p>
                            <p className="text-gray-500 text-sm">Store Credit Earned</p>
                          </div>
                        </div>
                        
                        {/* Pending Credit Section */}
                        {(creditInfo?.pending_credit > 0 || stats.pending_commission > 0) && (
                          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <p className="text-white/80 text-sm">Available to Claim</p>
                                <p className="text-3xl font-bold">${(creditInfo?.pending_credit || stats.pending_commission || 0).toFixed(2)}</p>
                              </div>
                              <Gift className="h-12 w-12 text-white/80" />
                            </div>
                            <Button 
                              onClick={claimCredit}
                              disabled={claiming}
                              className="w-full bg-white text-green-600 hover:bg-green-50 font-bold"
                            >
                              {claiming ? 'Claiming...' : 'Claim My Store Credit'}
                            </Button>
                          </div>
                        )}
                        
                        {/* Claimed Credit Codes */}
                        {creditInfo?.existing_credits?.length > 0 && (
                          <div className="bg-blue-50 rounded-xl p-4">
                            <p className="font-semibold text-blue-800 mb-2">Your Active Store Credit Codes:</p>
                            {creditInfo.existing_credits.map((credit, i) => (
                              <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 mb-2">
                                <div>
                                  <p className="font-mono font-bold text-[#003456]">{credit.code}</p>
                                  <p className="text-xs text-gray-500">
                                    {credit.expires_at && `Expires: ${new Date(credit.expires_at).toLocaleDateString()}`}
                                  </p>
                                </div>
                                <p className="text-lg font-bold text-green-600">${credit.remaining_balance.toFixed(2)}</p>
                              </div>
                            ))}
                            <p className="text-xs text-blue-600 mt-2">
                              Enter these codes in the "Store Credit" field at checkout to use your credit.
                            </p>
                          </div>
                        )}
                        
                        {/* Recently Claimed */}
                        {claimedCode && (
                          <div className="bg-green-100 border-2 border-green-500 rounded-xl p-4 text-center">
                            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                            <p className="font-semibold text-green-800">Store Credit Issued!</p>
                            <p className="text-2xl font-mono font-bold text-green-700 my-2">{claimedCode.credit_code}</p>
                            <p className="text-sm text-green-600">Amount: ${claimedCode.amount.toFixed(2)}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              Use this code at checkout. Expires: {new Date(claimedCode.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        Loading stats...
                      </div>
                    )}

                    <div className="mt-6 p-4 bg-[#CE8946]/10 rounded-xl">
                      <p className="text-sm text-gray-600">
                        <strong>How rewards work:</strong> Store credit is available 30 days after your friend's purchase is completed. Click "Claim My Store Credit" to get your credit code, then use it at checkout.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Create Referral Code */
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Already Have a Code?</h2>
                    <p className="text-gray-600 mb-4">Enter your email to check your referral stats.</p>
                    
                    <form onSubmit={checkExistingCode} className="space-y-4">
                      <Input
                        type="email"
                        required
                        value={checkEmail}
                        onChange={(e) => setCheckEmail(e.target.value)}
                        placeholder="Enter your email"
                      />
                      <Button 
                        type="submit" 
                        variant="outline"
                        className="w-full"
                        disabled={checking}
                      >
                        {checking ? 'Checking...' : 'Check My Code'}
                      </Button>
                    </form>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Get Your Referral Code</h2>
                    <p className="text-gray-600 mb-4">Sign up to start receiving store credit rewards!</p>
                    
                    {!hasOptedIn && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-amber-700">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          You must read and accept the program guidelines before getting your referral code.
                        </p>
                      </div>
                    )}
                    
                    <form onSubmit={createReferralCode} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                        <Input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="you@example.com"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className={`w-full ${hasOptedIn ? 'bg-[#003456] hover:bg-[#002B47]' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
                        disabled={loading}
                        data-testid="get-referral-code-btn"
                      >
                        {loading ? 'Creating...' : hasOptedIn ? 'Get My Referral Code' : 'Read Guidelines & Get Code'}
                      </Button>
                    </form>
                    
                    {hasOptedIn && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Program guidelines accepted
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Store Credit Example */}
              <div className="mt-12 bg-[#003456] rounded-2xl p-8 text-center text-white">
                <h2 className="text-2xl font-bold mb-4">Store Credit Reward Potential</h2>
                <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                  With {settings.referral_commission_percent}% store credit rewards on freeze dryer sales, here's what you could receive:
                </p>
                <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-lg font-semibold">D-Freeze Compact</p>
                    <p className="text-2xl font-bold text-[#CE8946]">${(1595 * settings.referral_commission_percent / 100).toFixed(2)}</p>
                    <p className="text-white/60 text-sm">store credit reward per referral</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-lg font-semibold">D-Freeze Pro</p>
                    <p className="text-2xl font-bold text-[#CE8946]">${(2495 * settings.referral_commission_percent / 100).toFixed(2)}</p>
                    <p className="text-white/60 text-sm">store credit reward per referral</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-lg font-semibold">D-Freeze XL</p>
                    <p className="text-2xl font-bold text-[#CE8946]">${(4995 * settings.referral_commission_percent / 100).toFixed(2)}</p>
                    <p className="text-white/60 text-sm">store credit reward per referral</p>
                  </div>
                </div>
                <p className="text-white/60 text-sm mt-6">
                  Store credit issued 30 days after friend's purchase completion. Valid for 12 months.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Program Guidelines Modal */}
      {showGuidelines && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-fade-in" data-testid="guidelines-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-[#003456] text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">D-Freeze Refer-a-Friend Program</h2>
                <p className="text-white/80 text-sm">Program Guidelines, Do's and Don'ts</p>
              </div>
              <button 
                onClick={() => setShowGuidelines(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div 
              className="flex-1 overflow-y-auto p-6"
              onScroll={(e) => {
                const el = e.target;
                if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
                  setGuidelinesRead(true);
                }
              }}
            >
              {/* Introduction */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-700 text-sm">
                  This document outlines the acceptable use of the D-Freeze Refer-a-Friend Program. The program is designed to reward customers who help grow the D-Freeze community while maintaining compliance with applicable promotional and tax regulations.
                </p>
                <p className="text-gray-700 text-sm mt-2 font-medium">
                  The referral program provides store credit rewards, not cash payments, and is intended as a customer appreciation promotion, not a commission-based sales program.
                </p>
              </div>

              {/* Program Overview */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#003456] mb-3">Program Overview</h3>
                <p className="text-gray-600 text-sm whitespace-pre-line">{programGuidelines.overview}</p>
              </div>

              {/* Do's Section */}
              <div className="mb-6">
                <button 
                  onClick={() => toggleSection('dos')}
                  className="w-full flex items-center justify-between bg-green-50 p-4 rounded-xl hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span className="text-lg font-bold text-green-800">Do's, Acceptable Participation</span>
                  </div>
                  {expandedSection === 'dos' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {expandedSection === 'dos' && (
                  <div className="mt-3 space-y-3 pl-4 border-l-4 border-green-200">
                    {programGuidelines.dos.map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
                        <h4 className="font-semibold text-green-700 mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Don'ts Section */}
              <div className="mb-6">
                <button 
                  onClick={() => toggleSection('donts')}
                  className="w-full flex items-center justify-between bg-red-50 p-4 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                    <span className="text-lg font-bold text-red-800">Don'ts, Prohibited Activities</span>
                  </div>
                  {expandedSection === 'donts' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {expandedSection === 'donts' && (
                  <div className="mt-3 space-y-3 pl-4 border-l-4 border-red-200">
                    {programGuidelines.donts.map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
                        <h4 className="font-semibold text-red-700 mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reward Timing */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#003456] mb-3">Reward Issuance and Timing</h3>
                <p className="text-gray-600 text-sm whitespace-pre-line">{programGuidelines.rewardTiming}</p>
              </div>

              {/* Expiration */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#003456] mb-3">Expiration of Referral Credits</h3>
                <p className="text-gray-600 text-sm whitespace-pre-line">{programGuidelines.expiration}</p>
              </div>

              {/* Compliance */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#003456] mb-3">Program Compliance & Modification</h3>
                <p className="text-gray-600 text-sm whitespace-pre-line">{programGuidelines.compliance}</p>
              </div>

              {/* Scroll indicator */}
              {!guidelinesRead && (
                <div className="text-center py-4 text-gray-400 text-sm animate-bounce">
                  ↓ Scroll to read all guidelines ↓
                </div>
              )}
            </div>
            
            {/* Footer with Accept Button */}
            <div className="border-t p-6 bg-gray-50">
              <div className="flex items-center gap-4">
                <input 
                  type="checkbox" 
                  id="accept-guidelines"
                  checked={guidelinesRead}
                  onChange={(e) => setGuidelinesRead(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <label htmlFor="accept-guidelines" className="text-sm text-gray-700 flex-1">
                  I have read and understand the D-Freeze Refer-a-Friend Program guidelines. I agree to participate in accordance with these terms.
                </label>
              </div>
              <div className="flex gap-3 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowGuidelines(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleOptIn}
                  disabled={!guidelinesRead}
                  className="flex-1 bg-[#003456] hover:bg-[#002B47] text-white disabled:opacity-50"
                  data-testid="accept-guidelines-btn"
                >
                  I Accept, Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ReferAFriend;
